<template>
    <div
        class="modal-template"
        :class="[
            {
                'modal-template--dark': darkOverlay,
            },
            mode
                ? `modal-template--mode-${mode}`
                : `modal-template--mode-default`,
        ]"
        @pointerdown.self="$emit('close')"
    >
        <div class="modal-template__inner">
            <button
                class="modal-template__btn-close"
                type="button"
                @click="$emit('close')"
            >
                <CloseIcon class="modal-template__btn-close-icon" />
            </button>

            <div class="modal-template__body custom-scrollbar">
                <slot></slot>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { onMounted, onBeforeUnmount } from 'vue';
    import CloseIcon from '@/components/commons/icons/CloseIcon.vue';

    interface ModalTemplateProps {
        darkOverlay?: boolean;
        closeBtn?: boolean;
        scrollBodyLock?: boolean;
        mode?: 'gallery' | 'filter' | 'list';
    }

    const props = defineProps<ModalTemplateProps>();

    const emits = defineEmits(['close', 'openFilter', 'openList']);

    onMounted(() => {
        if (props.scrollBodyLock) document.body.style.overflow = 'hidden';
    });

    onBeforeUnmount(() => {
        document.body.style.overflow = 'auto';
    });
</script>

<style lang="less">
    @modal-padding-top: 109px;
    @modal-padding-bottom: 87px;
    @modal-padding-top-desktop: 24px;
    @modal-padding-bottom-mobile: 10px;
    @inner-padding-top-desktop: 32px;
    @filter-modal-padding-top: 89px;
    @filter-modal-padding-bottom: 55px;
    @filter-modal-padding-top-desktop: 73px;
    @filter-modal-padding-bottom-desktop: 89px;

    .modal-template {
        position: fixed;
        left: 0;
        top: 0;
        width: 100vw;
        height: 100%;
        max-height: 100%;
        display: flex;
        align-items: flex-start;
        justify-content: center;
        z-index: 100000000000;
        padding: @modal-padding-top 64px @modal-padding-bottom;
        overflow-y: auto;
        overflow-x: hidden;
        scrollbar-width: none;

        @media (max-width: @size-desktop-top) {
            padding-top: @modal-padding-top-desktop;
        }

        @media (max-width: @size-mobile-top) {
            padding-right: 10px;
            padding-left: 10px;
            padding-bottom: @modal-padding-bottom-mobile;
        }

        // .modal-template--map-inset
        &--map-inset {
        }

        // .modal-template--hide
        &--hide {
            opacity: 0;
            user-select: none;
            pointer-events: none;
        }

        // .modal-template--dark
        &--dark {
            background-color: rgba(@dark-brown, 0.7);
        }

        &--mode-default {
            align-items: flex-start;
            padding: @modal-padding-top 64px @modal-padding-bottom;

            @media (max-width: @size-desktop-top) {
                padding-top: @modal-padding-top-desktop;
            }

            @media (max-width: @size-mobile-top) {
                padding-right: 10px;
                padding-left: 10px;
                padding-bottom: @modal-padding-bottom-mobile;
            }

            // .modal-template-inner__body
            .modal-template__body {
                background-color: @simple-light;
            }

            .modal-template__inner {
                max-width: 1184px;

                @media (max-width: @size-desktop-top) {
                    padding-top: @inner-padding-top-desktop;
                    max-width: 640px;
                }
            }

            .modal-template__btn-close {
                @media (max-width: @size-desktop-top) {
                    top: 0;
                    right: 0;
                    bottom: 100%;
                    background-color: transparent;
                }
            }
        }

        &--mode-gallery {
            align-items: center;
            padding: 10px 64px 10px;
            overflow-y: auto;
            overflow-x: hidden;

            @media (max-width: @size-desktop-top) {
                padding: 0;
            }
        }

        &--mode-list {
            align-items: flex-start;
            padding: 0;

            // .modal-template-inner__body
            .modal-template__body {
                height: 100vh;
                max-height: none;
                background-color: @simple-light;
                overflow-y: auto;
                overflow-x: hidden;
            }
        }

        &--mode-filter {
            align-items: flex-start;
            padding: @filter-modal-padding-top 105px
                @filter-modal-padding-bottom;

            @media (max-width: @size-desktop-top) {
                padding: @filter-modal-padding-top-desktop 105px
                    @filter-modal-padding-bottom-desktop;
            }

            @media (max-width: @size-mobile-top) {
                padding: 0;
            }

            .modal-template__inner {
                max-width: 1184px;

                @media (max-width: @size-desktop-top) {
                    max-width: 640px;
                }
            }

            // .modal-template-inner__body
            .modal-template__body {
                background-color: @simple-light;

                @media (max-width: @size-mobile-top) {
                    height: 100vh;
                    max-height: none;
                }
            }
        }

        // .modal-template__actions
        &__actions {
        }

        // .modal-template__inner
        &__inner {
            position: relative;
            width: 100%;
            height: auto;
        }

        // .modal-template__btn-close
        &__btn-close {
            position: absolute;
            top: 20px;
            right: 20px;
            z-index: 10;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            width: 32px;
            height: 32px;
            border: 0;
            background-color: rgba(@simple-light, 0.8);
            border-radius: 100%;
            padding: 0;
        }

        &__btn-close-icon {
            width: 32px;
            height: 32px;
            color: @warning;
        }

        // .modal-template-inner__body
        &__body {
        }

        // .modal-template__btn-filter
        &__btn-filter {
        }

        // .modal-template__btn-list
        &__btn-list {
        }
    }
</style>
